import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldAlert, ExternalLink, Users, Car, FileText, Image as ImageIcon, Clock, ChevronLeft, LogOut } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { BrandLockup } from "@/components/brand";
import { Panel, Spinner, Tag, Btn, EmptyState } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { useAsync } from "@/lib/hooks";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime, formatReg } from "@/lib/vda";

const STATUS_TONE = { draft: "muted", submitted: "amber", in_progress: "amber", closed: "green" };
const KIND_LABEL = { third_party: "Third party", passenger: "Passenger", witness: "Witness" };

function Section({ icon: Icon, title, count, children, testId }) {
  return (
    <Panel className="p-0 overflow-hidden" data-testid={testId}>
      <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold inline-flex items-center gap-2">{Icon && <Icon className="h-4 w-4 text-muted-foreground" />}{title}</h2>
        {count !== undefined && <span className="font-mono text-sm text-muted-foreground">{count}</span>}
      </div>
      <div className="p-5">{children}</div>
    </Panel>
  );
}
const Info = ({ l, v }) => (<div><div className="vda-tick text-muted-foreground">{l}</div><div className="mt-0.5 text-sm">{v || "—"}</div></div>);
const YN = (b) => (b === true ? "Yes" : b === false ? "No" : "—");

export default function AccidentReportView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { role, signOut } = useAuth();
  const isFM = role === "external_fleet_manager";
  const { data, loading } = useAsync(() => api.staffAccidentGet(id), [id]);

  const Shell = ({ children }) => {
    if (!isFM) return <StaffShell>{children}</StaffShell>;
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-20 bg-card border-b border-border">
          <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
            <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="acc-fm-back"><ChevronLeft className="h-4 w-4" /> Back</button>
            <BrandLockup compact />
            <button onClick={signOut} className="text-muted-foreground hover:text-foreground" title="Sign out"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className="max-w-5xl mx-auto p-5">{children}</main>
      </div>
    );
  };

  if (loading) return <Shell><Spinner /></Shell>;
  if (!data?.report) return <Shell><EmptyState title="Accident report not found" /></Shell>;
  const r = data.report;
  const byKind = (k) => data.parties.filter((p) => p.party_kind === k);
  const sketches = data.evidence.filter((e) => e.evidence_kind === "sketch");
  const photos = data.evidence.filter((e) => e.evidence_kind !== "sketch");

  return (
    <Shell>
      <div data-testid="accident-report-view" className="space-y-5">
        {/* Header / summary */}
        <Panel className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="vda-reg text-xs text-muted-foreground inline-flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-[hsl(var(--vda-red))]" />{r.human_reference}</div>
              <h1 className="text-xl font-semibold text-foreground mt-0.5">Accident &amp; bodywork report</h1>
              <p className="text-sm text-muted-foreground">{r.customers?.name} ({r.customers?.customer_code})</p>
            </div>
            <div className="flex items-center gap-2">
              <Tag tone={STATUS_TONE[r.status] || "muted"}>{r.status?.replace(/_/g, " ")}</Tag>
              {data.is_internal && data.booking_request_id && (
                <Btn data-testid="acc-open-request" onClick={() => nav(`/staff/booking/${data.booking_request_id}`)}><ExternalLink className="h-4 w-4" /> Open linked request</Btn>
              )}
            </div>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            <div>
              <div className="vda-tick text-muted-foreground">Vehicle</div>
              <button data-testid="acc-vehicle-link" onClick={() => r.vehicle_id && nav(`/staff/vehicle/${r.vehicle_id}`)} className="mt-0.5 text-primary hover:underline text-left text-sm">
                {[r.vehicles?.registration && formatReg(r.vehicles.registration), [r.vehicles?.make, r.vehicles?.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "—"}
              </button>
            </div>
            <Info l="Driver" v={r.drivers?.full_name} />
            <Info l="Driver mobile" v={r.drivers?.mobile_number} />
            <Info l="When" v={fmtDateTime(r.incident_at)} />
            <Info l="Where" v={[r.location_text, r.postcode].filter(Boolean).join(", ")} />
            <Info l="Submitted" v={fmtDateTime(r.submitted_at)} />
          </div>
        </Panel>

        <div className="grid lg:grid-cols-2 gap-5 items-start">
          {/* Circumstances */}
          <Section icon={FileText} title="Incident circumstances" testId="acc-circumstances">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <Info l="Injuries reported" v={YN(r.injuries_reported)} />
              <Info l="Police attended" v={r.police_attended ? (r.police_reference || "Yes") : "No"} />
              <Info l="Vehicle driveable" v={YN(r.vehicle_driveable)} />
              <Info l="Recovery required" v={YN(r.recovery_required)} />
            </div>
            {r.how_occurred && <div className="mt-3"><div className="vda-tick text-muted-foreground">How it occurred</div><p className="mt-1 text-sm">{r.how_occurred}</p></div>}
            {r.our_vehicle?.damage_areas && <div className="mt-3"><div className="vda-tick text-muted-foreground">Damage areas</div><p className="mt-1 text-sm">{r.our_vehicle.damage_areas}</p></div>}
            {r.description && <div className="mt-3"><div className="vda-tick text-muted-foreground">Description</div><p className="mt-1 text-sm">{r.description}</p></div>}
          </Section>

          {/* Parties */}
          <Section icon={Users} title="People involved" count={data.parties.length} testId="acc-parties">
            {data.parties.length === 0 ? <p className="text-sm text-muted-foreground">No passengers, third parties or witnesses recorded.</p> : (
              ["third_party", "passenger", "witness"].map((k) => byKind(k).length > 0 && (
                <div key={k} className="mb-4 last:mb-0">
                  <div className="vda-tick text-muted-foreground mb-1.5">{KIND_LABEL[k]}s</div>
                  <ul className="space-y-2">
                    {byKind(k).map((p) => (
                      <li key={p.id} className="vda-panel p-3 text-sm" data-testid={`acc-party-view-${p.id}`}>
                        <strong>{p.name || "Unnamed"}</strong>
                        {p.contact_phone && <span className="text-muted-foreground"> · {p.contact_phone}</span>}
                        {p.vehicle_reg && <div className="text-xs text-muted-foreground mt-0.5">Vehicle {p.vehicle_reg}{p.insurer ? ` · ${p.insurer}` : ""}{p.policy_ref ? ` · ${p.policy_ref}` : ""}</div>}
                        {p.injury_description && <div className="text-xs text-[hsl(var(--vda-red))] mt-0.5">Injury: {p.injury_description}</div>}
                        {p.notes && <div className="text-xs text-muted-foreground mt-0.5">{p.notes}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </Section>
        </div>

        {/* Sketch */}
        <Section icon={Car} title="Accident sketch" testId="acc-sketch">
          {sketches.length === 0 ? <p className="text-sm text-muted-foreground">No sketch provided with this report.</p> : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {sketches.map((s) => (<a key={s.id} href={s.url} target="_blank" rel="noreferrer" className="block"><img src={s.url} alt={s.caption || "sketch"} className="w-full rounded-[6px] border border-border" /></a>))}
            </div>
          )}
        </Section>

        {/* Evidence photos */}
        <Section icon={ImageIcon} title="Photographs & evidence" count={photos.length} testId="acc-evidence-view">
          {photos.length === 0 ? <p className="text-sm text-muted-foreground">No photos attached.</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((e) => (
                <a key={e.id} href={e.url} target="_blank" rel="noreferrer" className="block group" data-testid={`acc-photo-${e.id}`}>
                  {e.url ? <img src={e.url} alt={e.caption || "evidence"} className="w-full h-28 object-cover rounded-[6px] border border-border group-hover:opacity-90" /> : <div className="w-full h-28 grid place-items-center rounded-[6px] border border-border text-xs text-muted-foreground">unavailable</div>}
                  {e.caption && <div className="text-[10px] text-muted-foreground truncate mt-1">{e.caption}</div>}
                </a>
              ))}
            </div>
          )}
        </Section>

        {/* Updates + timeline */}
        {data.updates.length > 0 && (
          <Section icon={Clock} title="Subsequent updates" count={data.updates.length} testId="acc-updates">
            <ul className="space-y-3">
              {data.updates.map((u, i) => (
                <li key={i} className="border-l-2 border-border pl-3">
                  <div className="text-xs text-muted-foreground">{fmtDateTime(u.created_at)} · {u.public_status || u.internal_status}</div>
                  <div className="text-sm">{data.is_internal ? (u.internal_note || u.public_message) : u.public_message}</div>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {data.timeline.length > 0 && (
          <Section icon={Clock} title="Vehicle timeline" count={data.timeline.length} testId="acc-timeline">
            <ul className="space-y-2">
              {data.timeline.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="font-mono text-[10px] text-muted-foreground w-24 flex-none pt-0.5">{fmtDate(t.event_at)}</span>
                  <span><strong>{t.title}</strong>{t.body ? <span className="text-muted-foreground"> — {t.body}</span> : null}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </Shell>
  );
}
